import { C as Cart } from './cart-DZS7CGyq.js';
import { P as PubSub } from './pubsub-COBwjfLh.js';
import { p as parseHTML } from './parseHTML-Om2c1Ayj.js';
import './uniq-BDEfcc0N.js';
import './notyf-DIw0RvBj.js';

const EVENT_SOURCE = 'cart-gifts-with-purchase';
const SYNC_SOURCE = 'cart-gifts-sync';
const FETCH_SECTION = 'fetch-cart-gifts-with-purchase';
const MAX_SYNC_ITERATIONS = 5;

class CartGiftsWithPurchase extends HTMLElement {
  #buttons = [];
  #giftTiers = [];
  #currentSelectedGiftLineItems = [];
  #changeAbortController = new AbortController();
  #unsubsribers = [];
  #eventData = { source: EVENT_SOURCE };

  /**
   * Add/remove gifts from the JSON config. Static so it runs on pages
   * without this element. Uses the cart payload from the event when possible
   * to avoid an extra Cart.get().
   */
  static initSync() {
    if (!document.getElementById('cart-gifts-config')) return;

    ['cart:add', 'cart:change', 'cart:update'].forEach((event) => {
      PubSub.subscribe(event, (data) => {
        if (data?.source === EVENT_SOURCE || data?.source === SYNC_SOURCE) return;
        CartGiftsWithPurchase.#syncFromConfig(data, 0, {
          afterUserAction: CartGiftsWithPurchase.#isFullCart(data),
        });
      });
    });
    CartGiftsWithPurchase.#syncFromConfig();
  }

  static #readGiftTiers() {
    const script = document.getElementById('cart-gifts-config');
    if (!script?.textContent) return [];

    try {
      const { tiers } = JSON.parse(script.textContent);
      return Array.isArray(tiers) ? tiers : [];
    } catch {
      return [];
    }
  }

  static #hasOptedOut(tierId) {
    return localStorage.getItem(`gift-with-purchase-${tierId}`) !== null;
  }

  static #isFullCart(data) {
    return Boolean(data?.items) && typeof data.total_price === 'number';
  }

  static #isGiftIneligible(item, tiers, totalPrice) {
    const tier = tiers.find((candidate) => candidate.id === +item.properties._gwp_id);
    if (!tier || !tier.variants.includes(item.id)) return true;

    const threshold = +item.properties._gwp_threshold || tier.threshold;
    return totalPrice < threshold;
  }

  static #isOnCartPage() {
    return Boolean(document.querySelector('cart-gifts-with-purchase'));
  }

  static #shouldSkipSections(afterUserAction) {
    if (!CartGiftsWithPurchase.#isOnCartPage()) return true;
    return afterUserAction;
  }

  static async #refreshSections() {
    await Cart.update({
      hideMessage: true,
      skipSections: false,
      updates: {},
      event_data: { source: SYNC_SOURCE },
    });
  }

  /**
   * Drop line items left by the previous gift logic (`_dk_gift`).
   * @param {Array<{ key: string, properties?: object }>} items
   * @param {Record<string, number>} updates
   * @returns {void}
   */
  static #queueLegacyGiftRemoval(items, updates) {
    items.forEach((item) => {
      if (!Object.hasOwn(item.properties ?? {}, '_dk_gift')) return;
      updates[item.key] = 0;
    });
  }

  static #giftsToAdd(cart, giftItems, tiers) {
    return tiers
      .filter((tier) => cart.total_price >= tier.threshold)
      .filter((tier) => {
        const hasGift = giftItems.some((item) => +item.properties._gwp_id === tier.id);
        return !hasGift && !CartGiftsWithPurchase.#hasOptedOut(tier.id) && tier.variants[0];
      })
      .map((tier) => ({
        id: tier.variants[0],
        quantity: 1,
        properties: {
          _gwp: true,
          _gwp_threshold: tier.threshold,
          _gwp_id: tier.id,
        },
      }));
  }

  static async #syncFromConfig(cart, depth = 0, { afterUserAction = false } = {}) {
    if (depth >= MAX_SYNC_ITERATIONS) return false;
    if (!document.getElementById('cart-gifts-config')) return false;

    const currentCart = CartGiftsWithPurchase.#isFullCart(cart) ? cart : await Cart.get();
    const tiers = CartGiftsWithPurchase.#readGiftTiers();
    const giftItems = currentCart.items.filter((item) => item.properties?._gwp);
    const updates = {};
    const skipSections = CartGiftsWithPurchase.#shouldSkipSections(afterUserAction);
    let changed = false;

    CartGiftsWithPurchase.#queueLegacyGiftRemoval(currentCart.items, updates);

    giftItems.forEach((item) => {
      if (CartGiftsWithPurchase.#isGiftIneligible(item, tiers, currentCart.total_price)) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length > 0) {
      changed = true;
      const updatedCart = await Cart.update({
        hideMessage: true,
        skipSections,
        updates,
        event_data: { source: SYNC_SOURCE },
      });
      changed =
        (await CartGiftsWithPurchase.#syncFromConfig(updatedCart, depth + 1, {
          afterUserAction,
        })) || changed;
    } else {
      const items = CartGiftsWithPurchase.#giftsToAdd(currentCart, giftItems, tiers);

      if (items.length > 0) {
        changed = true;
        await Cart.add({
          items,
          hideMessage: true,
          skipSections,
          event_data: { source: SYNC_SOURCE },
        });
        changed =
          (await CartGiftsWithPurchase.#syncFromConfig(null, depth + 1, { afterUserAction })) ||
          changed;
      }
    }

    if (depth === 0 && afterUserAction && changed) {
      await CartGiftsWithPurchase.#refreshSections();
    }

    return changed;
  }

  async connectedCallback() {
    if (!Cart.sections.includes(FETCH_SECTION)) {
      Cart.sections.push(FETCH_SECTION);
    }

    this.#changeAbortController = new AbortController();
    this.#buttons = Array.from(this.querySelectorAll('button[data-cart-gift-button]'));
    this.#giftTiers = this.querySelectorAll('[data-gift-tier]');

    this.#buttons.forEach((button) => {
      button.addEventListener('click', (e) => this.#handleButtonClick(e), {
        signal: this.#changeAbortController.signal,
      });
    });

    this.#unsubsribers = ['cart:update', 'cart:change'].map((event) =>
      PubSub.subscribe(event, (data) => {
        if (data?.source === 'cart-gifts-with-purchase') return;
        this.#handleCartChange(data);
      })
    );
    this.#unsubsribers.push(
      PubSub.subscribe('cart:add', (data) => {
        if (data?.source === 'cart-gifts-with-purchase') return;
        this.#handleCartAdd(data);
      })
    );

    await this.#getInitialGiftLineItems();
    this.#setPressedFromCart();
  }

  disconnectedCallback() {
    this.#changeAbortController.abort();
    this.#unsubsribers.forEach((unsubscriber) => unsubscriber());
  }

  async #getInitialGiftLineItems() {
    const cart = await Cart.get();
    this.#currentSelectedGiftLineItems = cart.items.filter((item) => item.properties._gwp);
  }

  #setPressedFromCart() {
    this.#giftTiers.forEach((tier) => {
      const tierId = +tier.dataset.giftTier;
      const tierItemInCart = this.#currentSelectedGiftLineItems.find(
        (item) => +item.properties._gwp_id === tierId
      );
      if (!tierItemInCart) return;

      const matchingButton = Array.from(
        tier.querySelectorAll('button[data-cart-gift-button]')
      ).find(
        (button) =>
          button.dataset.value === `${tierItemInCart.properties._gwp_id}-${tierItemInCart.id}`
      );
      if (matchingButton) this.#setPressed(matchingButton, true);
    });
  }

  async #handleButtonClick(event) {
    const button = event.currentTarget;
    const { value, name, threshold } = button.dataset;

    const [tierId, variantId] = value.split('-');
    const selectedGiftLineItemFromTierIndex = this.#currentSelectedGiftLineItems.findIndex(
      (item) => +item.properties._gwp_id === +tierId && item.id === +variantId
    );

    this.#setLoading(button, true);
    try {
      if (selectedGiftLineItemFromTierIndex !== -1) {
        await this.#removeTierFromCart(selectedGiftLineItemFromTierIndex);
        this.#setPressed(button, false);
        this.#currentSelectedGiftLineItems.splice(selectedGiftLineItemFromTierIndex, 1);
        localStorage.setItem(`gift-with-purchase-${tierId}`, false);
      } else {
        await this.#addOrModifyTierInCart({ value, threshold });
        this.#buttons
          .filter((otherButton) => otherButton !== button && otherButton.dataset.name === name)
          .forEach((otherButton) => this.#setPressed(otherButton, false));
        this.#setPressed(button, true);
      }
    } finally {
      this.#setLoading(button, false);
    }
  }

  #setPressed(button, pressed) {
    button.setAttribute('aria-pressed', String(pressed));
  }

  #setLoading(button, loading) {
    const overlay = button.querySelector('.loading-overlay');
    if (overlay) overlay.ariaHidden = !loading;
  }

  async #removeTierFromCart(selectedGiftIndex) {
    await Cart.update({
      hideMessage: true,
      updates: { [this.#currentSelectedGiftLineItems[selectedGiftIndex].key]: 0 },
      event_data: this.#eventData,
    });
  }

  async #addOrModifyTierInCart({ value, threshold }) {
    const tierId = +value.split('-')[0];
    const newSelectedGiftLineItem = this.#createLineItemFromInput({ value, threshold });

    const selectedGiftLineItemFromTierIndex = this.#currentSelectedGiftLineItems.findIndex(
      (item) => +item.properties._gwp_id === tierId
    );

    if (selectedGiftLineItemFromTierIndex !== -1) {
      await Cart.update({
        hideMessage: true,
        skipSections: true,
        updates: { [this.#currentSelectedGiftLineItems[selectedGiftLineItemFromTierIndex].key]: 0 },
        event_data: this.#eventData,
      });
    }

    const addResult = await Cart.add({
      items: [newSelectedGiftLineItem],
      hideMessage: true,
      event_data: this.#eventData,
    });

    if (Array.isArray(addResult.items)) {
      const indexOfNewItem =
        selectedGiftLineItemFromTierIndex === -1
          ? this.#currentSelectedGiftLineItems.length
          : selectedGiftLineItemFromTierIndex;
      this.#currentSelectedGiftLineItems[indexOfNewItem] = addResult.items[0];
      localStorage.removeItem(`gift-with-purchase-${tierId}`);
    }
  }

  #handleCartChange(data) {
    this.#renderSection(data.sections);
  }

  #handleCartAdd(data) {
    this.#renderSection(data.sections);
  }

  #renderSection(sections) {
    const sectionHtml = sections?.[FETCH_SECTION];
    if (!sectionHtml) return;

    const target = parseHTML(sectionHtml).querySelector('cart-gifts-with-purchase');
    if (!target) return;
    this.replaceWith(target);
  }

  #createLineItemFromInput({ value, threshold }) {
    const [tierId, variantId] = value.split('-');
    return {
      id: +variantId,
      quantity: 1,
      properties: {
        _gwp: true,
        _gwp_threshold: +threshold,
        _gwp_id: +tierId,
      },
    };
  }
}

customElements.define('cart-gifts-with-purchase', CartGiftsWithPurchase);

CartGiftsWithPurchase.initSync();
